import React, { useState, useEffect } from 'react';
import {
  FiEdit,
  FiSave,
  FiX,
  FiPlus,
  FiTrash2,
  FiCheckCircle,
  FiAlertCircle,
  FiDownload,
  FiFilter
} from 'react-icons/fi';
import { FaCalendarAlt } from 'react-icons/fa';
import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CERTIFICATE_TYPES = [
  { value: '', label: '— Select Type —' },
  { value: 'TENTH_CERTIFICATE', label: '10th Certificate' },
  { value: 'TWELFTH_CERTIFICATE', label: '12th Certificate' },
  { value: 'DIPLOMA_CERTIFICATE', label: 'Diploma Certificate' },
  { value: 'DEGREE_CERTIFICATE', label: 'Degree Certificate' },
  { value: 'POST_GRADUATION_CERTIFICATE', label: 'Post Graduation Certificate' },
  { value: 'PHD_CERTIFICATE', label: 'PhD Certificate' },
  { value: 'EXPERIENCE_CERTIFICATE', label: 'Experience Certificate' },
  { value: 'RELIEVING_LETTER', label: 'Relieving Letter' },
  { value: 'SALARY_CERTIFICATE', label: 'Salary Certificate' },
  { value: 'TECHNICAL_CERTIFICATION', label: 'Technical Certification' },
  { value: 'PROFESSIONAL_CERTIFICATION', label: 'Professional Certification' },
  { value: 'TRAINING_CERTIFICATE', label: 'Training Certificate' },
  { value: 'AADHAAR_CARD', label: 'Aadhaar Card' },
  { value: 'PAN_CARD', label: 'PAN Card' },
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'DRIVING_LICENSE', label: 'Driving License' },
  { value: 'VOTER_ID', label: 'Voter ID' },
  { value: 'MEDICAL_CERTIFICATE', label: 'Medical Certificate' },
  { value: 'CHARACTER_CERTIFICATE', label: 'Character Certificate' },
  { value: 'CASTE_CERTIFICATE', label: 'Caste Certificate' },
  { value: 'INCOME_CERTIFICATE', label: 'Income Certificate' },
  { value: 'RESIDENCE_CERTIFICATE', label: 'Residence Certificate' },
  { value: 'OTHER', label: 'Other' },
];

const EMPTY_CERT = {
  certificateName: '', certificateType: '', issuingAuthority: '',
  issueDate: '', expiryDate: '', remarks: '',
};

const EmployeeProfile = () => {
  const [activeTab, setActiveTab] = useState('professional');
  const [editMode, setEditMode] = useState({
    professional: false,
    address: false,
    documents: false
  });

  // Employee data
  const [employeeData, setEmployeeData] = useState({
    id: null,
    name: "",
    firstName: "",
    lastName: "",
    position: "",
    email: "",
    phone: "",
    employeeId: "",
    department: "",
    joinDate: "",
    workLocation: "",
    manager: "",
    employmentType: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    address: "",
    addressLine1: "",
    city: "",
    state: "",
    country: "",
    bankName: "",
    accountNumber: "",
  });

  const [salaryData, setSalaryData] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [certificateSummary, setCertificateSummary] =
    useState('');

  const [certificates, setCertificates] = React.useState([
    {
      ...EMPTY_CERT,
      file: null,
    }
  ]);

  const [certSubmitting, setCertSubmitting] = React.useState(false);

  const [certToast, setCertToast] = React.useState(null);

  const [showCertForm, setShowCertForm] = React.useState(false);

  const [certificateFilter, setCertificateFilter] = useState('ALL');

  const [showCertificateFilters, setShowCertificateFilters] =
    useState(false);

  const [certificateSearch, setCertificateSearch] =
    useState('');
  const [showCertificateSummary, setShowCertificateSummary] =
    useState(false);



  const showCertToast = (type, msg) => {
    setCertToast({ type, msg });
    setTimeout(() => setCertToast(null), 4000);
  };

  const fetchCertificates = async (type = certificateFilter) => {
    try {
      let endpoint = '/certificate/my-certificates';
      if (type === 'EDUCATIONAL') endpoint = '/certificate/my-certificates/educational';
      if (type === 'EXPERIENCE') endpoint = '/certificate/my-certificates/experience';

      const response = await api.get(endpoint);
      console.log('CERTIFICATES:', response.data);
      const data = response.data?.data || response.data?.content || response.data || [];
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      showCertToast('error', 'Failed to load certificates');
    }
  };

  const fetchCertificateSummary = async () => {
    try {
      const response = await api.get('/certificate/my-summary');
      console.log('CERTIFICATE SUMMARY:', response.data);
      const data = response.data?.data || response.data?.content || response.data || '';
      setCertificateSummary(typeof data === 'string' ? data : '');
    } catch (err) {
      console.error('SUMMARY ERROR:', err);
    }
  };

  const handleCertChange = (index, e) => {

    const { name, value } = e.target;

    const updated = [...certificates];

    updated[index][name] = value;

    setCertificates(updated);
  };

  const handleCertFileChange = (index, e) => {

    const updated = [...certificates];

    updated[index].file =
      e.target.files[0] || null;

    setCertificates(updated);
  };

  const handleAddMore = () => {

    setCertificates([
      ...certificates,
      {
        ...EMPTY_CERT,
        file: null,
      }
    ]);
  };

  const handleRemoveCertificate = (index) => {

    if (certificates.length === 1) return;

    const updated =
      certificates.filter(
        (_, i) => i !== index
      );

    setCertificates(updated);
  };

  const handleCertSubmit = async (e) => {

    e.preventDefault();

    setCertSubmitting(true);

    try {

      for (const cert of certificates) {

        if (!cert.certificateName.trim()) {

          showCertToast(
            'error',
            'Certificate Name is required'
          );

          return;
        }

        if (!cert.certificateType) {

          showCertToast(
            'error',
            'Please select Certificate Type'
          );

          return;
        }

        const fd = new FormData();

        fd.append(
          'certificateName',
          cert.certificateName
        );

        fd.append(
          'certificateType',
          cert.certificateType
        );

        if (cert.issuingAuthority) {

          fd.append(
            'issuingAuthority',
            cert.issuingAuthority
          );
        }

        if (cert.issueDate) {

          fd.append(
            'issueDate',
            cert.issueDate
          );
        }

        if (cert.expiryDate) {

          fd.append(
            'expiryDate',
            cert.expiryDate
          );
        }

        if (cert.remarks) {

          fd.append(
            'remarks',
            cert.remarks
          );
        }

        if (cert.file) {

          fd.append(
            'file',
            cert.file
          );
        }

        await api.post(
          '/certificate/upload',
          fd,
          {
            headers: {
              'Content-Type':
                'multipart/form-data',
            },
          }
        );
      }

      showCertToast(
        'success',
        'Certificates uploaded successfully!'
      );
      await fetchCertificates();

      await fetchCertificateSummary();

      setCertificates([
        {
          ...EMPTY_CERT,
          file: null,
        }
      ]);

      setShowCertForm(false);

    } catch (err) {

      console.error(err);

      showCertToast(
        'error',
        'Upload failed'
      );

    } finally {

      setCertSubmitting(false);
    }
  };
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleEditToggle = (section) => {
    setEditMode({
      ...editMode,
      [section]: !editMode[section]
    });
  };

  const handleInputChange = (e, field) => {
    setEmployeeData({
      ...employeeData,
      [field]: e.target.value
    });
  };

  const handleSave = async (section) => {
    try {
      const payload = {
        firstName: employeeData.firstName,
        lastName: employeeData.lastName,
        phoneNumber: employeeData.phone,
        addressLine1: employeeData.addressLine1,
        city: employeeData.city,
        state: employeeData.state,
        country: employeeData.country,
        emergencyContactName: employeeData.emergencyContactName,
        emergencyContactPhone: employeeData.emergencyContactPhone,
      };
      await api.put('/user/update', payload);
      toast.success('Profile updated successfully');
      setEditMode({ ...editMode, [section]: false });
      fetchProfile();
    } catch (err) {
      toast.error('Failed to update profile');
      console.error(err);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const newDoc = {
        id: documents.length + 1,
        name: file.name,
        uploadDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        type: file.type.split('/')[1].toUpperCase() || 'FILE'
      };
      setDocuments([...documents, newDoc]);
    }
  };

  const handleDeleteDocument = (id) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  const handleDownloadCertificate =
    async (certificateId) => {

      try {

        const response =
          await api.get(
            `/certificate/download/${certificateId}`,
            {
              responseType: 'blob',
            }
          );

        const contentDisposition =
          response.headers[
          'content-disposition'
          ];

        let fileName =
          'certificate-file';

        if (contentDisposition) {

          const fileNameMatch =
            contentDisposition.match(
              /filename="?(.+)"?/
            );

          if (
            fileNameMatch?.length === 2
          ) {

            fileName =
              fileNameMatch[1];
          }
        }

        const blob =
          new Blob([response.data], {
            type:
              response.headers[
              'content-type'
              ],
          });

        const url =
          window.URL.createObjectURL(blob);

        const link =
          document.createElement('a');

        link.href = url;

        link.setAttribute(
          'download',
          fileName
        );

        document.body.appendChild(link);

        link.click();

        link.remove();

        window.URL.revokeObjectURL(url);

      } catch (err) {

        console.error(err);

        showCertToast(
          'error',
          'Failed to download certificate'
        );
      }
    };

  const fetchProfile = async () => {
    try {
      const res = await api.get('/user/profile');
      const data = res.data?.data || res.data?.content || res.data || {};
      setEmployeeData({
        ...employeeData,
        id: data.id || null,
        name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown User',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        position: data.jobTitle || '',
        email: data.email || '',
        phone: data.phoneNumber || '',
        employeeId: data.employeeId || '',
        department: data.department || '',
        joinDate: data.hireDate || '',
        workLocation: data.workLocation || '',
        manager: data.managerId ? `Manager ID: ${data.managerId}` : '',
        employmentType: data.employmentType || '',
        emergencyContactName: data.emergencyContactName || '',
        emergencyContactPhone: data.emergencyContactPhone || '',
        address: [data.addressLine1, data.city, data.state, data.country].filter(Boolean).join(', '),
        addressLine1: data.addressLine1 || '',
        city: data.city || '',
        state: data.state || '',
        country: data.country || '',
        bankName: data.bankName || '',
        accountNumber: data.bankAccountNumber || '',
      });

      if (data.id) {
        fetchSalary(data.id);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load profile');
    }
  };

  const fetchSalary = async (userId) => {
    try {
      const res = await api.get(`/finance/salary/${userId}/active`);
      const data = res.data?.data || res.data?.content || res.data;
      if (data) setSalaryData(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPayslips = async () => {
    try {
      const res = await api.get('/finance/payroll/my');
      const data = res.data?.data || res.data?.content || res.data || [];
      setPayslips(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchPayslips();
  }, []);

  useEffect(() => {
    fetchCertificates(certificateFilter);
    fetchCertificateSummary();
  }, [certificateFilter]);

  const filteredDocuments =
    documents.filter((doc) => {

      const search =
        certificateSearch.toLowerCase();

      const matchesSearch =
        (doc.certificateName?.toLowerCase() || '').includes(search) ||
        (doc.certificateType?.toLowerCase() || '').includes(search) ||
        (doc.issuingAuthority?.toLowerCase() || '').includes(search);

      if (
        certificateFilter === 'ALL'
      ) {
        return matchesSearch;
      }

      const educationalTypes = [

        'TENTH_CERTIFICATE',
        'TWELFTH_CERTIFICATE',
        'DIPLOMA_CERTIFICATE',
        'DEGREE_CERTIFICATE',
        'POST_GRADUATION_CERTIFICATE',
        'PHD_CERTIFICATE',
        'TRAINING_CERTIFICATE',
        'TECHNICAL_CERTIFICATION',
        'PROFESSIONAL_CERTIFICATION'
      ];

      const experienceTypes = [

        'EXPERIENCE_CERTIFICATE',
        'RELIEVING_LETTER',
        'SALARY_CERTIFICATE'
      ];

      if (
        certificateFilter === 'EDUCATIONAL'
      ) {

        return (
          educationalTypes.includes(
            doc.certificateType
          ) && matchesSearch
        );
      }

      if (
        certificateFilter === 'EXPERIENCE'
      ) {

        return (
          experienceTypes.includes(
            doc.certificateType
          ) && matchesSearch
        );
      }

      return matchesSearch;
    });

  const renderTabContent = () => {
    switch (activeTab) {
      case 'professional':
        return (
          <div className="space-y-4 animate-fade-in">
            <div className="flex justify-between items-center pb-4 border-b border-slate-100">
              <h3 className="text-xl font-bold text-slate-800">Professional Information</h3>
              {!editMode.professional ? (
                <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-sm font-bold transition-colors" onClick={() => handleEditToggle('professional')}>
                  <FiEdit /> Edit Phone
                </button>
              ) : (
                <div className="flex gap-2">
                  <button className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg text-sm font-bold transition-colors" onClick={() => handleSave('professional')}>
                    <FiSave /> Save
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-sm font-bold transition-colors" onClick={() => handleEditToggle('professional')}>
                    <FiX /> Cancel
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col py-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Department</label>
                <p className="text-sm font-medium text-slate-800">{employeeData.department || '-'}</p>
              </div>
              <div className="flex flex-col py-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Manager</label>
                <p className="text-sm font-medium text-slate-800">{employeeData.manager || '-'}</p>
              </div>
              <div className="flex flex-col py-2 md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Phone Number</label>
                {editMode.professional ? (
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    value={employeeData.phone}
                    onChange={(e) => handleInputChange(e, 'phone')}
                  />
                ) : (
                  <p className="text-sm font-medium text-slate-800">{employeeData.phone || '-'}</p>
                )}
              </div>
            </div>
          </div>
        );
      case 'address':
        return (
          <div className="space-y-4 animate-fade-in">
            <div className="flex justify-between items-center pb-4 border-b border-slate-100">
              <h3 className="text-xl font-bold text-slate-800">Home Address & Contact</h3>
              {!editMode.address ? (
                <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-sm font-bold transition-colors" onClick={() => handleEditToggle('address')}>
                  <FiEdit /> Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg text-sm font-bold transition-colors" onClick={() => handleSave('address')}>
                    <FiSave /> Save
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-sm font-bold transition-colors" onClick={() => handleEditToggle('address')}>
                    <FiX /> Cancel
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col py-2 md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Address Line 1</label>
                {editMode.address ? (
                  <input type="text" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" value={employeeData.addressLine1} onChange={(e) => handleInputChange(e, 'addressLine1')} />
                ) : <p className="text-sm font-medium text-slate-800">{employeeData.addressLine1 || '-'}</p>}
              </div>
              <div className="flex flex-col py-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">City</label>
                {editMode.address ? (
                  <input type="text" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" value={employeeData.city} onChange={(e) => handleInputChange(e, 'city')} />
                ) : <p className="text-sm font-medium text-slate-800">{employeeData.city || '-'}</p>}
              </div>
              <div className="flex flex-col py-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">State</label>
                {editMode.address ? (
                  <input type="text" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" value={employeeData.state} onChange={(e) => handleInputChange(e, 'state')} />
                ) : <p className="text-sm font-medium text-slate-800">{employeeData.state || '-'}</p>}
              </div>
              <div className="flex flex-col py-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Country</label>
                {editMode.address ? (
                  <input type="text" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" value={employeeData.country} onChange={(e) => handleInputChange(e, 'country')} />
                ) : <p className="text-sm font-medium text-slate-800">{employeeData.country || '-'}</p>}
              </div>
            </div>

            <h4 className="text-lg font-bold text-slate-800 mt-8 mb-4 border-b border-slate-100 pb-2">Emergency Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col py-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Name</label>
                {editMode.address ? (
                  <input type="text" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" value={employeeData.emergencyContactName} onChange={(e) => handleInputChange(e, 'emergencyContactName')} />
                ) : <p className="text-sm font-medium text-slate-800">{employeeData.emergencyContactName || '-'}</p>}
              </div>
              <div className="flex flex-col py-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Phone</label>
                {editMode.address ? (
                  <input type="text" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" value={employeeData.emergencyContactPhone} onChange={(e) => handleInputChange(e, 'emergencyContactPhone')} />
                ) : <p className="text-sm font-medium text-slate-800">{employeeData.emergencyContactPhone || '-'}</p>}
              </div>
            </div>
          </div>
        );
      case 'job':
        return (
          <div className="space-y-4 animate-fade-in">
            <h3 className="text-xl font-bold text-slate-800 pb-4 border-b border-slate-100">Job Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Employee ID</label><p className="text-sm font-medium text-slate-800">{employeeData.employeeId || '-'}</p></div>
              <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Position</label><p className="text-sm font-medium text-slate-800">{employeeData.position || '-'}</p></div>
              <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Department</label><p className="text-sm font-medium text-slate-800">{employeeData.department || '-'}</p></div>
              <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Employment Type</label><p className="text-sm font-medium text-slate-800">{employeeData.employmentType || '-'}</p></div>
              <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Work Location</label><p className="text-sm font-medium text-slate-800">{employeeData.workLocation || '-'}</p></div>
              <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Join Date</label><p className="text-sm font-medium text-slate-800">{employeeData.joinDate || '-'}</p></div>
            </div>
          </div>
        );
      case 'salary':
        return (
          <div className="space-y-4 animate-fade-in">
            <h3 className="text-xl font-bold text-slate-800 pb-4 border-b border-slate-100">Salary Information</h3>
            {salaryData ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Base Salary</label><p className="text-lg font-bold text-blue-600">${salaryData.baseSalary}</p></div>
                <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">HRA</label><p className="text-sm font-medium text-slate-800">${salaryData.hra}</p></div>
                <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Travel Allowance</label><p className="text-sm font-medium text-slate-800">${salaryData.travelAllowance}</p></div>
                <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Medical Allowance</label><p className="text-sm font-medium text-slate-800">${salaryData.medicalAllowance}</p></div>
                <div className="flex flex-col py-2"><label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Other Allowance</label><p className="text-sm font-medium text-slate-800">${salaryData.otherAllowance}</p></div>
                <div className="flex flex-col py-2"><label className="text-xs font-bold text-red-500 uppercase tracking-wider mb-1">PF Deduction</label><p className="text-sm font-medium text-red-600">-${salaryData.pfDeduction}</p></div>
                <div className="flex flex-col py-2"><label className="text-xs font-bold text-red-500 uppercase tracking-wider mb-1">Tax Percentage</label><p className="text-sm font-medium text-red-600">{salaryData.taxPercentage}%</p></div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-12 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                <FiAlertCircle className="text-4xl text-slate-300 mb-2" />
                <p className="text-slate-500 font-medium">No active salary structure found.</p>
              </div>
            )}
          </div>
        );
      case 'payslips':
        return (
          <div className="space-y-4 animate-fade-in">
            <h3 className="text-xl font-bold text-slate-800 pb-4 border-b border-slate-100">Payslips</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {payslips.length === 0 ? (
                <div className="col-span-1 md:col-span-2 flex flex-col items-center justify-center p-12 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                  <p className="text-slate-500 font-medium">No payslips generated yet.</p>
                </div>
              ) : payslips.map(ps => (
                <div className="flex flex-col bg-slate-50 border border-slate-100 rounded-lg p-4" key={ps.id}>
                  <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-200">
                    <h4 className="font-bold text-lg text-slate-800">{ps.month} / {ps.year}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${ps.status === 'PAID' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600'}`}>{ps.status}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-slate-500">Gross Salary</span>
                    <span className="font-bold text-slate-700">${ps.grossSalary}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500">Net Salary</span>
                    <span className="font-bold text-emerald-600">${ps.netSalary}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'documents':
        return (
          <div className="space-y-4 animate-fade-in relative pb-4">
            {/* Toast */}
            {certToast && (
              <div className={`flex items-center gap-2 p-4 rounded-lg text-sm font-medium ${certToast.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'} mb-4`}>
                {certToast.type === 'success' ? <FiCheckCircle size={16} /> : <FiAlertCircle size={16} />}
                <span>{certToast.msg}</span>
                <button onClick={() => setCertToast(null)} className="ml-auto opacity-70 hover:opacity-100"><FiX size={14} /></button>
              </div>
            )}

            <div className="flex justify-between items-center pb-4 border-b border-slate-100">
              <h3 className="text-xl font-bold text-slate-800">Documents &amp; Certificates</h3>
              <div className="flex gap-2">
                <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-sm font-bold transition-colors" onClick={() => setShowCertificateSummary(!showCertificateSummary)}>
                  Summary
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm font-bold transition-colors shadow-sm" onClick={() => setShowCertForm(!showCertForm)}>
                  <FiPlus /> {showCertForm ? 'Cancel' : 'Upload Document'}
                </button>
              </div>
            </div>

            {showCertificateSummary && (
              <div className="p-5 bg-blue-50 border border-blue-100 rounded-xl animate-fade-in">
                <h4 className="font-bold text-blue-800 mb-2">Certificate Summary</h4>
                <p className="text-sm text-blue-700 leading-relaxed whitespace-pre-wrap">{certificateSummary}</p>
              </div>
            )}

            <div className="flex items-center gap-3 relative">
              <div className="relative flex-1">
                <FiFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search certificates..."
                  className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all shadow-sm"
                  value={certificateSearch}
                  onChange={(e) => setCertificateSearch(e.target.value)}
                  onFocus={() => setShowCertificateFilters(true)}
                />
              </div>
              <button className="p-3 bg-white border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" onClick={() => setShowCertificateFilters(!showCertificateFilters)}>
                <FiFilter size={18} />
              </button>

              {showCertificateFilters && (
                <div className="absolute right-0 top-14 bg-white border border-slate-100 shadow-xl rounded-xl w-48 overflow-hidden z-20">
                  {['ALL', 'EDUCATIONAL', 'EXPERIENCE'].map(f => (
                    <button
                      key={f}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${certificateFilter === f ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                      onClick={() => { setCertificateFilter(f); setShowCertificateFilters(false); }}
                    >
                      {f === 'ALL' ? 'All Certificates' : f.charAt(0) + f.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Form */}
            {showCertForm && (
              <form className="p-6 bg-slate-50 border border-slate-200 rounded-2xl animate-fade-in" onSubmit={handleCertSubmit} noValidate>
                {certificates.map((cert, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6 pb-6 border-b border-slate-200 last:border-0 last:mb-0 last:pb-0">
                    <div className="flex flex-col gap-1 md:col-span-2">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Certificate Name <span className="text-red-500">*</span></label>
                      <input type="text" name="certificateName" placeholder="e.g. B.Tech Degree" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={cert.certificateName} onChange={(e) => handleCertChange(index, e)} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Certificate Type <span className="text-red-500">*</span></label>
                      <select name="certificateType" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={cert.certificateType} onChange={(e) => handleCertChange(index, e)}>
                        {CERTIFICATE_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Issuing Authority</label>
                      <input type="text" name="issuingAuthority" placeholder="e.g. JNTU" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={cert.issuingAuthority} onChange={(e) => handleCertChange(index, e)} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Issue Date</label>
                      <input type="date" name="issueDate" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={cert.issueDate} onChange={(e) => handleCertChange(index, e)} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Expiry Date</label>
                      <input type="date" name="expiryDate" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={cert.expiryDate} onChange={(e) => handleCertChange(index, e)} />
                    </div>
                    <div className="flex flex-col gap-1 md:col-span-2">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Remarks</label>
                      <textarea name="remarks" rows={2} placeholder="Notes..." className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={cert.remarks} onChange={(e) => handleCertChange(index, e)} />
                    </div>
                    <div className="flex flex-col gap-1 md:col-span-2">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Attach File</label>
                      <div className="flex items-center gap-3">
                        <label className="px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg text-sm font-medium cursor-pointer transition-colors whitespace-nowrap">
                          {cert.file ? cert.file.name : 'Choose file...'}
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => handleCertFileChange(index, e)} />
                        </label>
                        {cert.file && (
                          <button type="button" className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" onClick={() => {
                            const updated = [...certificates];
                            updated[index].file = null;
                            setCertificates(updated);
                          }}>
                            <FiX size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <button type="button" className="text-sm font-bold text-blue-600 hover:text-blue-700 mt-2 mb-6" onClick={handleAddMore}>+ Add More Documents</button>

                <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-sm" disabled={certSubmitting}>
                    {certSubmitting ? 'Uploading...' : 'Upload Certificates'}
                  </button>
                  <button type="button" className="px-6 py-2 bg-slate-200 text-slate-700 font-bold rounded-lg hover:bg-slate-300 transition-colors" onClick={() => {
                    if (certificates.length === 1) { setShowCertForm(false); setCertificates([{ ...EMPTY_CERT, file: null }]); }
                    else { setCertificates(certificates.slice(0, -1)); }
                  }}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Documents List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {filteredDocuments.length === 0 && <div className="col-span-1 md:col-span-2 py-8 text-center text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200 font-medium">No documents found.</div>}
              {filteredDocuments.map((doc) => (
                <div key={doc.id} className="bg-slate-50 border border-slate-100 rounded-lg p-4 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-bold text-slate-800 line-clamp-2 pr-2">{doc.certificateName}</h4>
                      <div className="flex gap-2 shrink-0">
                        <button className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors" onClick={() => handleDownloadCertificate(doc.id)} title="Download">
                          <FiDownload />
                        </button>
                        <button className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors" onClick={() => handleDeleteDocument(doc.id)} title="Delete">
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2 text-slate-600"><span className="w-20 font-medium text-slate-400">Type:</span> <span className="font-medium text-slate-800">{doc.certificateType}</span></div>
                      <div className="flex items-center gap-2 text-slate-600"><span className="w-20 font-medium text-slate-400">Authority:</span> <span>{doc.issuingAuthority || '-'}</span></div>
                      <div className="flex items-center gap-2 text-slate-600"><span className="w-20 font-medium text-slate-400">Issued:</span> <span>{doc.issueDate || '-'}</span></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (

    <div className="max-w-6xl mx-auto space-y-4 animate-fade-in pb-10 p-4">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left Column - Basic Info Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col p-5 items-center h-full">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 text-2xl font-bold mb-4">
              <span>{employeeData.name ? employeeData.name.substring(0, 2).toUpperCase() : 'EMP'}</span>
            </div>
            
            <h3 className="text-xl font-bold text-slate-800 mb-1 text-center">{employeeData.name || 'Unknown User'}</h3>
            <p className="text-slate-500 text-sm font-medium mb-4 text-center">{employeeData.position || 'No Position Set'}</p>
            
            <div className="w-full space-y-3 pt-4 border-t border-slate-100">
              <div className="flex flex-col">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Email Address</span>
                <span className="text-sm font-medium text-slate-700 truncate">{employeeData.email || '-'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Phone Number</span>
                <span className="text-sm font-medium text-slate-700">{employeeData.phone || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Tabs and Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
            {/* Tabs Header */}
            <div className="flex flex-wrap border-b border-slate-200 bg-white">
              {['professional', 'address', 'job', 'salary', 'payslips', 'documents'].map(tab => {
                const labels = {
                  professional: 'Professional',
                  address: 'Home Address',
                  job: 'Job Info',
                  salary: 'Salary',
                  payslips: 'Payslips',
                  documents: 'Documents'
                };
                return (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`px-4 py-3 font-bold text-sm transition-all border-b-2 ${activeTab === tab
                        ? 'border-blue-600 text-blue-600 bg-blue-50/30'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    {labels[tab]}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="p-4 md:p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeProfile;